/**
 * Privacy Policy — /privacy route (PUBLIC, unauthenticated).
 *
 * Long-form legal page for the Sahas Forge platform operated by Sahas Labs
 * Pvt Ltd. Mirrors the readable-article layout used by the marketing surfaces
 * (centered max-width column, BrandMark lockup at the top, a back-to-home
 * link) but is fully self-contained so it carries no marketing chrome /
 * pack-gated providers. Token-only Tailwind — no hardcoded colours — so it
 * inherits light/dark + tenant theming for free, and prints cleanly via the
 * global print styles in index.css.
 *
 * Content tailored to an industrial IoT + digital-twin B2B SaaS in early
 * access: covers operational/telemetry data, the controller↔processor split
 * for customer workspaces, sub-processors (AWS Mumbai, Google OIDC, AWS SES),
 * and India's DPDP Act 2023 + GDPR notice/consent obligations.
 */

import { useEffect, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { BrandMark } from "@/lib/ui/brand-mark";
import { Button } from "@/lib/ui/button";
import { useTenantTheme } from "@/lib/tenant-theme";

const EFFECTIVE_DATE = "10 June 2026";
const CONTACT_EMAIL = "info@sahaslabs.com";

interface TocEntry {
  id: string;
  label: string;
}

const TOC: readonly TocEntry[] = [
  { id: "introduction", label: "1. Introduction & who we are" },
  { id: "information-we-collect", label: "2. Information we collect" },
  { id: "how-we-use", label: "3. How we use information" },
  { id: "cookies", label: "4. Cookies & similar technologies" },
  { id: "legal-bases", label: "5. Legal bases & consent" },
  { id: "sharing", label: "6. How we share / sub-processors" },
  { id: "customer-data-role", label: "7. Customer data & our role" },
  { id: "data-location", label: "8. Data location & international transfers" },
  { id: "retention", label: "9. Data retention" },
  { id: "security", label: "10. Security" },
  { id: "your-rights", label: "11. Your rights" },
  { id: "grievance", label: "12. Grievance officer & contact" },
  { id: "children", label: "13. Children" },
  { id: "changes", label: "14. Changes to this policy" },
  { id: "contact", label: "15. Contact us" },
];

/** A titled policy section with a stable anchor id for the table of contents. */
function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section
      id={id}
      data-pdf-block
      className="flex scroll-mt-24 flex-col gap-3"
    >
      <h2 className="text-xl font-semibold tracking-tight text-on-surface md:text-2xl">
        {title}
      </h2>
      <div className="flex flex-col gap-3 text-sm leading-relaxed text-on-surface-muted md:text-base">
        {children}
      </div>
    </section>
  );
}

/** In-page table of contents — anchor links to each section. */
function TableOfContents() {
  return (
    <nav
      aria-label="On this page"
      data-no-print
      className="rounded-xl border border-border bg-surface-elevated/60 p-5"
    >
      <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-on-surface-muted">
        On this page
      </p>
      <ol className="flex flex-col gap-1.5">
        {TOC.map((entry) => (
          <li key={entry.id}>
            <a
              href={`#${entry.id}`}
              className="text-sm text-on-surface-muted transition-colors hover:text-brand hover:underline"
            >
              {entry.label}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

export function PrivacyPolicy() {
  const tenant = useTenantTheme();

  // Land at the top — these pages are reached from footer links across the
  // public site, often from a scrolled position.
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <div className="flex min-h-screen flex-col bg-surface text-on-surface">
      {/* Top bar — BrandMark links home; matches the auth/marketing header. */}
      <header
        data-no-print
        className="flex h-16 items-center justify-between border-b border-border px-4 md:px-10"
      >
        <Link to="/" className="flex items-center" aria-label={tenant.productName}>
          <BrandMark variant="full" className="h-9 md:h-10" />
        </Link>
        <Button variant="ghost" asChild>
          <Link to="/">
            <ArrowLeft className="mr-1.5 size-4" />
            Back to home
          </Link>
        </Button>
      </header>

      <main className="mx-auto flex w-full max-w-3xl flex-1 flex-col gap-8 px-4 py-12 md:py-16">
        {/* Title block */}
        <div className="flex flex-col gap-3">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand">
            Legal
          </p>
          <h1 className="text-3xl font-semibold leading-tight tracking-tight text-on-surface md:text-4xl">
            Privacy Policy
          </h1>
          <p className="text-sm text-on-surface-muted">
            Last updated: {EFFECTIVE_DATE} · Effective date: {EFFECTIVE_DATE}
          </p>
          <p className="max-w-2xl text-sm leading-relaxed text-on-surface-muted md:text-base">
            This Privacy Policy explains how Sahas Labs Pvt Ltd collects, uses,
            shares, and protects personal data in connection with the Sahas
            Forge platform, our website, and our related services. Please read
            it together with our{" "}
            <Link to="/terms" className="font-medium text-brand hover:underline">
              Terms of Service
            </Link>
            .
          </p>
          <Button
            variant="outline"
            size="sm"
            data-no-print
            className="w-fit"
            onClick={() => window.print()}
          >
            <Printer className="mr-1.5 size-4" />
            Print / save as PDF
          </Button>
        </div>

        <TableOfContents />

        <div className="flex flex-col gap-10">
          <Section id="introduction" title="1. Introduction & who we are">
            <p>
              Sahas Labs Pvt Ltd (&ldquo;Sahas Labs&rdquo;, &ldquo;we&rdquo;,
              &ldquo;us&rdquo;, or &ldquo;our&rdquo;) is the company that
              develops and operates Sahas Forge, an industrial Internet-of-Things
              (IoT) and digital-twin software-as-a-service platform built for
              foundries and process-manufacturing operations. Sahas Forge lets
              customers connect plant equipment, model their processes as living
              digital twins, monitor live and historical telemetry, trace
              material genealogy, and analyse the drivers of quality and yield.
            </p>
            <p>
              This Privacy Policy applies to personal data that we process
              through our public website, our web application, our
              application-programming interfaces, our gateway and connectivity
              tooling, and any other product, feature, or service that links to
              or references this policy (together, the &ldquo;Services&rdquo;).
              It describes the categories of personal data we collect, why we
              collect them, the legal bases on which we rely, how and with whom
              we share data, how long we keep it, the safeguards we apply, and
              the rights and choices available to you.
            </p>
            <p>
              Sahas Forge is a business-to-business platform that is currently
              offered on an early-access / beta basis. It is intended for use by
              organisations and their authorised personnel, and not by
              individual consumers for personal, household, or family purposes.
              Where our customer is an organisation, that organisation determines
              the purposes and means of processing the operational and end-user
              data held within its workspace; our respective roles are described
              in Section 7 (Customer data & our role).
            </p>
            <p>
              If you have any questions about this policy or about how we handle
              personal data, you can contact us at any time at{" "}
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="font-medium text-brand hover:underline"
              >
                {CONTACT_EMAIL}
              </a>
              .
            </p>
          </Section>

          <Section id="information-we-collect" title="2. Information we collect">
            <p>
              We collect personal data that you provide to us directly, data
              that is generated automatically when you use the Services, and data
              that our customers or their personnel submit to the platform. The
              categories below describe what we collect and, where helpful, why.
            </p>
            <p className="font-medium text-on-surface">
              (a) Identity &amp; account data.
            </p>
            <p>
              When you create an account or sign in, we collect your name and
              email address. Authentication is performed through Google / OpenID
              Connect (OIDC) using our identity provider (Zitadel); when you sign
              in with a federated provider, we receive your basic profile
              identifiers (such as your name, email address, and a unique subject
              identifier) from that provider so that we can establish and secure
              your session.
            </p>
            <p className="font-medium text-on-surface">
              (b) Registration &amp; approval data.
            </p>
            <p>
              Because access to Sahas Forge is reviewed and approved before it is
              granted, we collect additional information at sign-up to evaluate
              your request, including your full name, the company or organisation
              you represent, your job position or role, and your contact phone
              number. We use this information to verify that the request is
              legitimate, to decide whether to approve, decline, or limit access,
              and to maintain an auditable record of access decisions.
            </p>
            <p className="font-medium text-on-surface">
              (c) Customer operational data.
            </p>
            <p>
              In the course of using the platform, customers and their authorised
              users create and upload operational data. This may include plant
              and equipment models, process-graph definitions, process
              parameters and set-points, sensor and telemetry readings and time
              series, alarms and events, batches, heats, lots, and material
              genealogy records, quality and defect records, generated reports,
              and documents or files that users upload as attachments. This
              operational data may, in some cases, contain personal data (for
              example, the name of an operator associated with a shift, batch, or
              annotation). We process operational data on behalf of, and under
              the instructions of, the customer organisation, as described in
              Section 7.
            </p>
            <p className="font-medium text-on-surface">
              (d) Usage &amp; technical data.
            </p>
            <p>
              When you interact with the Services, we automatically collect
              technical and usage information, including server and application
              logs, Internet Protocol (IP) address, browser and device type and
              settings, operating system, referring and exit pages, the dates and
              timestamps of your activity, diagnostic and performance data, and
              error reports. We also use strictly-necessary session and
              authentication cookies to keep you signed in and to protect the
              integrity of your session (see Section 4).
            </p>
            <p className="font-medium text-on-surface">(e) Communications.</p>
            <p>
              If you contact us for support, submit a request, or otherwise
              communicate with us, we collect the content of those communications
              together with your contact details and any information you choose
              to include, so that we can respond, provide assistance, and keep a
              record of our correspondence.
            </p>
          </Section>

          <Section id="how-we-use" title="3. How we use information">
            <p>We use personal data for the following purposes:</p>
            <ul className="flex list-disc flex-col gap-2 pl-5">
              <li>
                to provide, operate, maintain, and deliver the Services,
                including hosting and displaying customer operational data and
                making the platform&rsquo;s features available to you;
              </li>
              <li>
                to authenticate users, establish and manage sessions, and verify
                identity through our OIDC sign-in;
              </li>
              <li>
                to review, approve, decline, limit, suspend, or revoke access
                requests, and to administer accounts and entitlements;
              </li>
              <li>
                to enforce tenant isolation, safeguard the confidentiality and
                integrity of each customer&rsquo;s workspace, and detect, prevent,
                and respond to security incidents, fraud, abuse, and other
                harmful or unlawful activity;
              </li>
              <li>
                to generate and deliver alerts, notifications, and transactional
                email (for example, account, security, approval, and
                system-status messages);
              </li>
              <li>
                to provide customer support and to respond to your enquiries and
                requests;
              </li>
              <li>
                to monitor, analyse, troubleshoot, and improve the performance,
                reliability, and usability of the Services, and to develop new
                features (using aggregated or de-identified data wherever
                practicable);
              </li>
              <li>
                to comply with applicable laws and regulatory obligations, to
                respond to lawful requests from public authorities, and to
                establish, exercise, or defend legal claims.
              </li>
            </ul>
          </Section>

          <Section id="cookies" title="4. Cookies & similar technologies">
            <p>
              We use only strictly-necessary cookies and equivalent
              browser-storage technologies that are required for the Services to
              function. These are limited to session and authentication cookies
              that keep you signed in, preserve your session state, and protect
              against session-related security threats. Without these cookies the
              Services cannot operate, and you cannot remain authenticated.
            </p>
            <p>
              We do not use third-party advertising cookies, cross-site tracking
              cookies, or behavioural-profiling technologies on the Services, and
              we do not sell or share data for cross-context behavioural
              advertising. Because the cookies we set are strictly necessary,
              they do not require consent under most applicable laws; however,
              you can configure your browser to block or delete cookies, with the
              understanding that doing so will prevent you from signing in and
              using the platform.
            </p>
          </Section>

          <Section id="legal-bases" title="5. Legal bases & consent">
            <p>
              We process personal data only where we have a valid legal basis to
              do so. Depending on the context and the law that applies to you,
              the legal bases we rely on include:
            </p>
            <ul className="flex list-disc flex-col gap-2 pl-5">
              <li>
                <span className="font-medium text-on-surface">
                  Performance of a contract
                </span>{" "}
                — to provide the Services you or your organisation have requested,
                to administer your account, and to fulfil our obligations under
                our agreements;
              </li>
              <li>
                <span className="font-medium text-on-surface">
                  Legitimate interests
                </span>{" "}
                — to secure the platform, prevent abuse, operate and improve the
                Services, and run our business, where those interests are not
                overridden by your rights and freedoms;
              </li>
              <li>
                <span className="font-medium text-on-surface">Consent</span> —
                where required by law for a specific purpose, in which case you
                may withdraw your consent at any time without affecting the
                lawfulness of processing carried out before withdrawal;
              </li>
              <li>
                <span className="font-medium text-on-surface">
                  Compliance with a legal obligation
                </span>{" "}
                — where we are required to process data to meet our legal,
                regulatory, tax, accounting, or audit obligations.
              </li>
            </ul>
            <p>
              For individuals in India, we provide notice and, where applicable,
              obtain consent in accordance with the Digital Personal Data
              Protection Act, 2023 (the &ldquo;DPDP Act&rdquo;) and the rules
              made under it. This Privacy Policy, together with the consent and
              notice presented at the point of collection, constitutes the notice
              required under the DPDP Act, and describes the personal data we
              process, the purposes of processing, and how you may exercise your
              rights and lodge a grievance. For individuals located in the
              European Economic Area (EEA) or the United Kingdom, we process
              personal data in accordance with the EU General Data Protection
              Regulation (GDPR) and the UK GDPR, relying on the legal bases set
              out above.
            </p>
          </Section>

          <Section id="sharing" title="6. How we share / sub-processors">
            <p>
              We do not sell personal data, and we do not rent, trade, or
              otherwise disclose personal data for the independent commercial
              purposes of third parties. We share personal data only as necessary
              to operate the Services, and only with the categories of recipients
              described below.
            </p>
            <p className="font-medium text-on-surface">Sub-processors.</p>
            <p>
              We rely on a small number of trusted service providers
              (sub-processors) to host and run the platform. Each sub-processor
              is bound by contractual obligations to process data only on our
              instructions and to maintain appropriate security. Our current
              sub-processors are:
            </p>
            <ul className="flex list-disc flex-col gap-2 pl-5">
              <li>
                <span className="font-medium text-on-surface">
                  Amazon Web Services (AWS)
                </span>{" "}
                — cloud hosting, compute, and storage, in the Mumbai region
                (ap-south-1), India;
              </li>
              <li>
                <span className="font-medium text-on-surface">Google</span> —
                OpenID Connect (OIDC) sign-in / federated authentication;
              </li>
              <li>
                <span className="font-medium text-on-surface">
                  Amazon Simple Email Service (AWS SES)
                </span>{" "}
                — delivery of transactional and notification email.
              </li>
            </ul>
            <p>
              We may also disclose personal data: (i) where required to do so by
              law, regulation, legal process, or an enforceable governmental or
              regulatory request; (ii) where necessary to enforce our agreements,
              investigate potential violations, or protect the rights, property,
              safety, or security of Sahas Labs, our customers, our users, or the
              public; and (iii) in connection with, or during negotiations of, a
              merger, acquisition, financing, reorganisation, sale of assets, or
              other business transaction, in which case personal data may be
              transferred to the successor or acquiring entity subject to the
              commitments in this policy.
            </p>
          </Section>

          <Section id="customer-data-role" title="7. Customer data & our role">
            <p>
              Sahas Forge is a business-to-business platform. Where we provide the
              Services to a customer organisation, that organisation is the data
              controller (and, under the DPDP Act, the data fiduciary) in respect
              of the operational data and end-user personal data held within its
              workspace, and it determines the purposes and means of that
              processing. Sahas Labs acts as a data processor (data processor /
              data fiduciary&rsquo;s processor) and processes such data on the
              customer&rsquo;s documented instructions and on its behalf.
            </p>
            <p>
              As between Sahas Labs and the customer, the customer is responsible
              for ensuring that it has a valid legal basis and any required
              notices or consents for the personal data it submits to the
              platform, for the accuracy and lawfulness of that data, and for
              responding to requests from individuals whose data it controls. If
              you are an end user whose data is held in a customer&rsquo;s
              workspace and you wish to exercise your rights, please contact the
              relevant customer organisation directly; we will assist that
              organisation as required.
            </p>
            <p>
              For customers that require it, a Data Processing Addendum (DPA)
              setting out our respective obligations as processor and controller
              is available on request at{" "}
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="font-medium text-brand hover:underline"
              >
                {CONTACT_EMAIL}
              </a>
              .
            </p>
          </Section>

          <Section
            id="data-location"
            title="8. Data location & international transfers"
          >
            <p>
              Personal data and customer operational data are stored and
              processed primarily on AWS infrastructure located in the Mumbai
              region (ap-south-1) in India. We design the platform so that
              customer operational data is, by default, hosted within this
              region.
            </p>
            <p>
              Some limited processing may involve recipients or infrastructure
              located outside India — for example, certain authentication or
              email-delivery functions provided by our sub-processors. Where
              personal data is transferred across borders, we take steps to
              ensure that the transfer is carried out in accordance with
              applicable law and that the data continues to be protected by
              appropriate safeguards, such as contractual data-protection
              commitments with the recipient. By using the Services, you
              acknowledge that your data may be processed in the locations
              described in this policy.
            </p>
          </Section>

          <Section id="retention" title="9. Data retention">
            <p>
              We retain personal data only for as long as necessary to fulfil the
              purposes for which it was collected, including to provide the
              Services, to comply with our legal, regulatory, accounting, and
              audit obligations, and to resolve disputes and enforce our
              agreements.
            </p>
            <ul className="flex list-disc flex-col gap-2 pl-5">
              <li>
                <span className="font-medium text-on-surface">Account data</span>{" "}
                is retained for as long as your account remains active and for a
                reasonable period thereafter as required for legitimate business
                or legal purposes.
              </li>
              <li>
                <span className="font-medium text-on-surface">
                  Operational telemetry and time-series data
                </span>{" "}
                are retained in accordance with the retention policy configured
                for the relevant workspace or plan, after which the data is
                deleted, aggregated, or compressed.
              </li>
              <li>
                <span className="font-medium text-on-surface">
                  Registration and approval records
                </span>{" "}
                are retained for audit and security purposes for as long as
                reasonably necessary to evidence access decisions.
              </li>
              <li>
                <span className="font-medium text-on-surface">
                  Logs and diagnostic data
                </span>{" "}
                are retained for a limited period appropriate to security
                monitoring and troubleshooting, and are then deleted or
                anonymised.
              </li>
            </ul>
            <p>
              When personal data is no longer required, we delete it or
              irreversibly anonymise it, unless a longer retention period is
              required or permitted by law.
            </p>
          </Section>

          <Section id="security" title="10. Security">
            <p>
              We take the security of personal data seriously and implement
              appropriate technical and organisational measures designed to
              protect it against unauthorised access, disclosure, alteration, and
              destruction. These measures include encryption of data in transit
              using Transport Layer Security (TLS), access controls and
              least-privilege principles, logical isolation of each customer
              tenant&rsquo;s data, authentication through OpenID Connect, and
              monitoring and logging of access to our systems.
            </p>
            <p>
              Notwithstanding these measures, no method of transmission over the
              Internet and no method of electronic storage is completely secure,
              and we cannot guarantee absolute security. Security is a shared
              responsibility: you and your organisation are responsible for
              safeguarding account credentials, managing user access
              appropriately, and using the Services in a secure manner. If we
              become aware of a personal-data breach that affects you, we will
              act in accordance with applicable law, including any obligation to
              notify the relevant data-protection authority and affected
              individuals.
            </p>
          </Section>

          <Section id="your-rights" title="11. Your rights">
            <p>
              Subject to the applicable law and to your relationship with us, you
              may have the following rights in respect of your personal data:
            </p>
            <ul className="flex list-disc flex-col gap-2 pl-5">
              <li>
                the right to access the personal data we hold about you and to
                obtain a summary of how it is processed;
              </li>
              <li>
                the right to correction and completion of inaccurate or
                incomplete personal data;
              </li>
              <li>
                the right to erasure of your personal data where it is no longer
                necessary for the purposes for which it was collected or where
                you withdraw consent on which processing relies;
              </li>
              <li>
                the right to data portability — to receive or download certain
                personal data in a structured, commonly-used format;
              </li>
              <li>
                the right to withdraw consent at any time, where processing is
                based on consent;
              </li>
              <li>
                the right of grievance redressal and the right to nominate, in
                the event of death or incapacity, another individual to exercise
                your rights, as provided under the DPDP Act; and
              </li>
              <li>
                for individuals in the EEA or UK, the additional rights to
                restrict or object to processing and to lodge a complaint with a
                supervisory authority.
              </li>
            </ul>
            <p>
              To exercise any of these rights, please contact us at{" "}
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="font-medium text-brand hover:underline"
              >
                {CONTACT_EMAIL}
              </a>
              . We will respond to verified requests within the timelines
              required by applicable law. We may need to verify your identity
              before acting on a request, and where the personal data is held
              within a customer&rsquo;s workspace, we may direct your request to,
              or act under the instructions of, the relevant customer
              organisation.
            </p>
          </Section>

          <Section id="grievance" title="12. Grievance officer & contact">
            <p>
              In accordance with the Digital Personal Data Protection Act, 2023
              and applicable rules, you may direct any grievance, question, or
              concern regarding the processing of your personal data, or the
              exercise of your rights, to our grievance contact:
            </p>
            <div
              data-pdf-block
              className="rounded-xl border border-border bg-surface-elevated/60 p-5 text-sm leading-relaxed text-on-surface-muted"
            >
              <p className="font-medium text-on-surface">Sahas Labs Pvt Ltd</p>
              <p>
                Attn: Grievance Officer / Data Protection contact
                <br />
                Email:{" "}
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="font-medium text-brand hover:underline"
                >
                  {CONTACT_EMAIL}
                </a>
              </p>
            </div>
            <p>
              We will acknowledge and address grievances within the timelines
              prescribed by applicable law. If you are not satisfied with our
              response, you may have the right to escalate the matter to the
              competent data-protection authority.
            </p>
          </Section>

          <Section id="children" title="13. Children">
            <p>
              Sahas Forge is a business platform intended for use by
              organisations and their authorised personnel. It is not directed
              to, marketed to, or intended for use by children, and we do not
              knowingly collect personal data from individuals under the age of
              eighteen (18). If you believe that a child has provided us with
              personal data, please contact us at{" "}
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="font-medium text-brand hover:underline"
              >
                {CONTACT_EMAIL}
              </a>{" "}
              and we will take appropriate steps to delete such data.
            </p>
          </Section>

          <Section id="changes" title="14. Changes to this policy">
            <p>
              We may update this Privacy Policy from time to time to reflect
              changes in our practices, our Services, or applicable law. When we
              make changes, we will revise the &ldquo;Last updated&rdquo; date at
              the top of this page and post the updated policy here. Where the
              changes are material, we will take reasonable steps to notify you,
              for example by email or through a notice within the Services. Your
              continued use of the Services after an updated policy takes effect
              constitutes your acknowledgement of the changes, to the extent
              permitted by applicable law.
            </p>
          </Section>

          <Section id="contact" title="15. Contact us">
            <p>
              If you have any questions, requests, or concerns about this Privacy
              Policy or our handling of personal data, please contact us at:
            </p>
            <div
              data-pdf-block
              className="rounded-xl border border-border bg-surface-elevated/60 p-5 text-sm leading-relaxed text-on-surface-muted"
            >
              <p className="font-medium text-on-surface">Sahas Labs Pvt Ltd</p>
              <p>
                Email:{" "}
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="font-medium text-brand hover:underline"
                >
                  {CONTACT_EMAIL}
                </a>
              </p>
            </div>
            <p className="text-xs text-on-surface-muted">
              This Privacy Policy is effective as of {EFFECTIVE_DATE} and is
              governed by the laws of India.
            </p>
          </Section>
        </div>

        {/* Footer nav */}
        <div
          data-no-print
          className="mt-4 flex flex-col items-start justify-between gap-3 border-t border-border pt-6 text-sm text-on-surface-muted sm:flex-row sm:items-center"
        >
          <span>
            © {new Date().getFullYear()} Sahas Labs Pvt Ltd. All rights
            reserved.
          </span>
          <div className="flex items-center gap-4">
            <Link className="hover:text-on-surface" to="/terms">
              Terms of Service
            </Link>
            <Link className="hover:text-on-surface" to="/">
              Home
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
